import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useScroll } from '../contexts/ScrollContext'
import styles from './Navigation.module.css'

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { scrollY } = useScroll()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    setIsScrolled(scrollY > 50)
  }, [scrollY])

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string, targetPath: string = '/boxos') => {
    e.preventDefault()
    setIsMobileMenuOpen(false) // Close mobile menu

    // If not on the target page, navigate there first
    if (location.pathname !== targetPath) {
      navigate(targetPath)
      setTimeout(() => {
        const element = document.getElementById(targetId)
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top + window.scrollY
          const offsetPosition = elementPosition - offset
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
      }, 100)
    } else {
      const element = document.getElementById(targetId)
      if (element) {
        const offset = 80 // Account for fixed nav height
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - offset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }
    }
  }

  const handleGetStarted = () => {
    setIsMobileMenuOpen(false) // Close mobile menu

    if (location.pathname === '/solutions') {
      const element = document.getElementById('solutions-contact')
      if (element) {
        const offset = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - offset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
      return
    }

    if (location.pathname !== '/boxos') {
      navigate('/boxos')
      setTimeout(() => {
        const element = document.getElementById('contact')
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top + window.scrollY
          window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
        }
      }, 100)
    } else {
      const element = document.getElementById('contact')
      if (element) {
        const offset = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
      }
    }
  }

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setIsMobileMenuOpen(false) // Close mobile menu

    // Navigate to the pre-landing page
    navigate('/')
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const isPreLanding = location.pathname === '/'

  if (isPreLanding) {
    return null
  }

  const isSolutionsPage = location.pathname === '/solutions'
  const crossPageLink = isSolutionsPage
    ? { href: '/boxos', label: 'Box Editor' }
    : { href: '/solutions', label: 'Custom Solutions' }

  const sectionLinks = isSolutionsPage
    ? [
        { id: 'what-we-build', label: 'What We Build', path: '/solutions' },
        { id: 'our-approach', label: 'Our Approach', path: '/solutions' },
        { id: 'who-this-is-for-business', label: "Who It's For", path: '/solutions' },
      ]
    : [
        { id: 'the-problem', label: 'The Problem', path: '/boxos' },
        { id: 'the-difference', label: 'The Difference', path: '/boxos' },
        { id: 'who-this-is-for', label: "Who It's For", path: '/boxos' },
      ]
  const contactLink = isSolutionsPage
    ? { id: 'solutions-contact', path: '/solutions' }
    : { id: 'contact', path: '/boxos' }

  return (
    <>
    <a href="#main-content" className={styles.skipLink}>Skip to main content</a>
    <nav className={`${styles.nav} ${isScrolled ? styles.scrolled : ''}`}>
      <div className="container">
        <div className={styles.navContent}>
          {/* Logo */}
          <a href="/" className={styles.logoLink} onClick={handleHomeClick}>
            <img
              src="/boxos logo.png"
              alt="Box.OS - Rapid Results, Unbeatable"
              className={styles.logo}
            />
          </a>

          {/* Navigation Links - Desktop */}
          <div className={styles.navLinks}>
            {sectionLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={styles.navLink}
                onClick={(e) => scrollToSection(e, link.id, link.path)}
              >
                {link.label}
              </a>
            ))}
            {!isSolutionsPage && (
              <a href={`#${contactLink.id}`} className={styles.navLink} onClick={(e) => scrollToSection(e, contactLink.id, contactLink.path)}>Join the Waitlist</a>
            )}
            <a
              href={crossPageLink.href}
              className={styles.navLink}
              onClick={(e) => {
                e.preventDefault()
                setIsMobileMenuOpen(false)
                navigate(crossPageLink.href)
              }}
            >
              {crossPageLink.label}
            </a>
          </div>

          {/* CTA Button - Desktop */}
          <button className={styles.ctaButton} onClick={handleGetStarted}>
            Contact Us
          </button>

          {/* Mobile Menu Button */}
          <button
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.open : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}>
        <div className={styles.mobileMenuContent}>
          {sectionLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={styles.mobileNavLink}
              onClick={(e) => scrollToSection(e, link.id, link.path)}
            >
              {link.label}
            </a>
          ))}
          {!isSolutionsPage && (
            <a href={`#${contactLink.id}`} className={styles.mobileNavLink} onClick={(e) => scrollToSection(e, contactLink.id, contactLink.path)}>Join the Waitlist</a>
          )}
          <a
            href={crossPageLink.href}
            className={styles.mobileNavLink}
            onClick={(e) => {
              e.preventDefault()
              setIsMobileMenuOpen(false)
              navigate(crossPageLink.href)
            }}
          >
            {crossPageLink.label}
          </a>
          <button className={styles.mobileCtaButton} onClick={handleGetStarted}>
            Contact Us
          </button>
        </div>
      </div>
    </nav>
    </>
  )
}

export default Navigation
